import * as React from 'react';
import { useInvoiceStore } from 'pages/tracking-v2/invoice-details-page/hooks';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import {
  IndicatorStatusIconAndLabel,
  ReturnStatusIconAndLabel,
} from 'pages/tracking-v2/shared-components';
import {
  getInvoiceOrigin,
  getInvoiceDestination,
} from 'pages/tracking-v2/utils/invoice-utils';
import { cn } from 'lib/utils';
import { Formatter } from 'utils';

import { InfoColumn } from './components';

type Props = React.ComponentProps<typeof Card>

const DATE_FALLBACK = '__/__/____';

export const InvoiceInfoBar = ({ children, ...props }: Props) => {
  const { invoice } = useInvoiceStore();

  const companyName = React.useMemo(() => {
    return invoice?.company?.tradeName?.toUpperCase() || '---';
  }, [invoice?.company]);

  const origin = React.useMemo(() => {
    if (!invoice) return '---';
    return getInvoiceOrigin(invoice).asCityAndUfText().toUpperCase();
  }, [invoice]);

  const destination = React.useMemo(() => {
    if (!invoice) return '---';
    return getInvoiceDestination(invoice).asCityAndUfText().toUpperCase();
  }, [invoice]);

  const emissionDate = React.useMemo(() => {
    return invoice?.infnfeIdeDhemi
      ? Formatter.date(invoice?.infnfeIdeDhemi)
      : DATE_FALLBACK;
  }, [invoice?.infnfeIdeDhemi]);

  const deadlineDate = React.useMemo(() => {
    return invoice?.deadlineDate
      ? Formatter.date(invoice?.deadlineDate)
      : DATE_FALLBACK;
  }, [invoice?.deadlineDate]);

  const deliveryForecastDate = React.useMemo(() => {
    return invoice?.meta?.deliveryForecastDate
      ? Formatter.date(invoice.meta.deliveryForecastDate)
      : DATE_FALLBACK;
  }, [invoice?.meta?.deliveryForecastDate]);

  const deliveryDate = React.useMemo(() => {
    return invoice?.deliveryDate
      ? Formatter.date(invoice.deliveryDate)
      : DATE_FALLBACK;
  }, [invoice?.deliveryDate]);

  const lastTruckLocation = React.useMemo(() => {
    const lastLocation = invoice?.geolocation?.[invoice?.geolocation?.length - 1];
    return lastLocation?.description || '---';
  }, [invoice?.geolocation]);

  return (
    <Card {...props}>
      <CardContent className={'flex flex-row pt-6 items-start 2xl:items-center'}>
        <div className={cn(
          'grid grid-cols-2 lg:grid-cols-4 gap-4 w-full items-start',
          '2xl:flex 2xl:flex-row 2xl:gap-6 2xl:max-w-[calc(100%-48px)] overflow-hidden',
        )}>
          {!invoice && (
            <>
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className={'flex flex-col gap-1'}>
                  <Skeleton key={index} className={'w-[60px] h-[14px]'} />
                  <Skeleton key={index} className={'w-[90px] h-3'} />
                </div>
              ))}
            </>
          )}
          {invoice && (
            <>
              <InfoColumn>
                {invoice?.meta?.indicatorStatus && (
                  <IndicatorStatusIconAndLabel status={invoice.meta.indicatorStatus} />
                )}
                {invoice?.returnDate && <ReturnStatusIconAndLabel />}
              </InfoColumn>
              <InfoColumn>
                <InfoColumn.Title>Centro</InfoColumn.Title>
                <InfoColumn.Content>{companyName}</InfoColumn.Content>
              </InfoColumn>
              <InfoColumn>
                <InfoColumn.Title>Origem</InfoColumn.Title>
                <InfoColumn.Content>{origin}</InfoColumn.Content>
              </InfoColumn>
              <InfoColumn>
                <InfoColumn.Title>Destino</InfoColumn.Title>
                <InfoColumn.Content>{destination}</InfoColumn.Content>
              </InfoColumn>
              <InfoColumn>
                <InfoColumn.Title>Emissão</InfoColumn.Title>
                <InfoColumn.Content>{emissionDate}</InfoColumn.Content>
              </InfoColumn>
              <InfoColumn>
                <InfoColumn.Title>Prazo</InfoColumn.Title>
                <InfoColumn.Content>{deadlineDate}</InfoColumn.Content>
              </InfoColumn>
              <InfoColumn>
                <InfoColumn.Title>Previsão de entrega</InfoColumn.Title>
                <InfoColumn.Content>{deliveryForecastDate}</InfoColumn.Content>
              </InfoColumn>
              <InfoColumn>
                <InfoColumn.Title>Entrega</InfoColumn.Title>
                <InfoColumn.Content>{deliveryDate}</InfoColumn.Content>
              </InfoColumn>
              <InfoColumn className={'col-span-2 lg:col-span-4'}>
                <InfoColumn.Title>Última localização</InfoColumn.Title>
                <InfoColumn.Content>{lastTruckLocation}</InfoColumn.Content>
              </InfoColumn>
            </>
          )}
        </div>
        <div className={'flex items-center gap-2 shrink-0 ml-auto'}>
          {children}
        </div>
      </CardContent>
    </Card>
  );
};